import { createContext, useCallback, useContext, useEffect, useState, ReactNode } from 'react';
import { setCsrfTokenForAxios } from './csrf-utils.js';

export type CsrfContextType = {
  csrfToken: string | null;
  csrfError: unknown | null;
  refreshCsrfToken: () => Promise<void>;
  clearCsrfToken: () => void;
};

const CsrfContext = createContext<CsrfContextType>({
  csrfToken: null,
  csrfError: null,
  refreshCsrfToken: async () => {},
  clearCsrfToken: () => {},
});

export const useCsrf = () => useContext(CsrfContext);

export type CsrfProviderProps = {
  children: ReactNode;
};

export const CsrfProvider: React.FC<CsrfProviderProps> = ({ children }) => {
  const [csrfToken, setCsrfToken] = useState<string | null>(null);
  const [csrfError, setCsrfError] = useState<unknown | null>(null);

  const fetchCsrfToken = useCallback(async () => {
    try {
      const baseURL = import.meta.env.VITE_API_URL || 'http://localhost:3000/';
      const url = new URL('/csrf-token', baseURL).href;
      const res = await fetch(url, {
        credentials: 'include',
      });
      if (!res.ok) {
        throw new Error(`Failed to fetch CSRF token: ${res.status}`);
      }
      const data = await res.json();
      setCsrfToken(data.csrfToken);
      setCsrfTokenForAxios(data.csrfToken);
      setCsrfError(null);
    } catch (error) {
      console.error('Failed to fetch CSRF token', error);
      setCsrfError(error);
      throw error;
    }
  }, []);

  const clearCsrfToken = useCallback(() => {
    setCsrfToken(null);
    setCsrfTokenForAxios(null);
  }, []);

  useEffect(() => {
    void fetchCsrfToken().catch(() => undefined);
  }, [fetchCsrfToken]);

  return (
    <CsrfContext.Provider value={{ csrfToken, csrfError, refreshCsrfToken: fetchCsrfToken, clearCsrfToken }}>
      {children}
    </CsrfContext.Provider>
  );
};
